import { TooltipV2 } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useInternalComments } from '../../queries/use-internal-comments';
import { InternalCommentOverview } from '../../types/internal-comment';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import { getThreadContext } from '../internal-comments/utils';
import { ChatBubbleOvalLeftIcon } from '@heroicons/react/24/outline';
import UserAvatar from '../../components/avatar/UserAvatar';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import SummaryTableTitle from './SummaryTableTitle';

function ActiveInternalComments(): JSX.Element {
    const { id } = useWorkpaperContext();
    const internalComments = useInternalComments(id, {
        filter: 'status=ACTIVE',
        search: '',
        sort: 'sort[dir]=asc&sort[field]=createdAt',
    });

    return (
        <div>
            <div className="border-b pb-3 flex justify-between items-center">
                <SummaryTableTitle
                    title="Internal Comments"
                    count={internalComments.data ? (internalComments.data.data.length as number).toString() : '0'}
                    status="Active"
                    statusBadgeColor="yellow"
                />
                <Link to="../internal-comments" className="text-xs font-medium text-gray-700">
                    See all
                </Link>
            </div>
            {internalComments.isLoading && <></>}
            {internalComments.isError && <p className="block text-center text-sm text-gray-500 pt-4">Unable to get internal comments.</p>}
            {internalComments.isSuccess && (
                <div className="max-h-[350px] overflow-auto">
                    {internalComments.data.data.length === 0 ? (
                        <div className="mt-12">
                            <EmptyStateNew
                                icon={<ChatBubbleOvalLeftIcon />}
                                title="No active comments"
                                titleSize="small"
                                helpText="See any outstanding comments preventing certification."
                                helpLink="https://learn.cimplico.com/docs/dashboard-overview#common-blockers"
                            />
                        </div>
                    ) : (
                        <div className="mt-4">
                            {internalComments.data.data.map((ic) => {
                                return <InternalComment key={ic.id} internalComment={ic} />;
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

type InternalCommentProps = {
    internalComment: InternalCommentOverview;
};
function InternalComment({ internalComment }: InternalCommentProps): JSX.Element {
    const { firstName, lastName, profileHexColour, avatarUrl } = internalComment.createdBy;
    const context = getThreadContext(internalComment);

    const msInDay = 86_400_000;

    const dayDifference = Math.floor((new Date().getTime() - new Date(internalComment.createdAt).getTime()) / msInDay);

    const dateColour = dayDifference > 7 ? 'text-red-700' : dayDifference >= 5 ? 'text-yellow-600' : 'text-gray-800';

    return (
        <Link
            className="flex items-end justify-between mt-2 gap-8 hover:bg-gray-100 p-2"
            to={`../internal-comments?activeComment=${internalComment.id}`}
        >
            <div className="w-[70%]">
                <div className="flex items-center">
                    <UserAvatar user={{ firstName, lastName, avatarUrl, profileHexColour }} />
                    <div className="ml-2">
                        <div className="flex items-start">
                            {context ? (
                                <Link to={context.url} className="text-xs text-indigo-600 hover:underline">
                                    {context.label}
                                </Link>
                            ) : (
                                <p className="text-xs text-gray-600">-</p>
                            )}
                        </div>
                        <p className="text-sm text-gray-800 line-clamp-1">{internalComment.rawComment}</p>
                    </div>
                </div>
            </div>
            <div>
                <TooltipV2 message={formatDateTime(internalComment.createdAt)} position="left">
                    <p className={`${dateColour} text-sm`}>{calcRelativeTime(internalComment.createdAt, true)}</p>
                </TooltipV2>
            </div>
        </Link>
    );
}

export default ActiveInternalComments;
